/**
 * Relative Strength Index (RSI)
 *
 * Momentum oscillator measuring speed and magnitude of price changes.
 * Ranges 0–100; default overbought = 70, oversold = 30.
 * Uses Wilder smoothing (same as EMA with α = 1/period).
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const rsiIndicator: IndicatorDefinition = {
  id: "rsi",
  name: "Relative Strength Index",
  shortName: "RSI",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 1, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "overbought", label: "Overbought", type: "number", default: 70, min: 50, max: 100 },
    { key: "oversold", label: "Oversold", type: "number", default: 30, min: 0, max: 50 },
    { key: "color", label: "Color", type: "color", default: "#AA00FF" },
  ],
  plots: [
    { key: "rsi", label: "RSI", color: "#AA00FF", style: "line", lineWidth: 2, priceScaleId: "rsi" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    if (src.length < period + 1) return [{ key: "rsi", data }];

    let avgGain = 0;
    let avgLoss = 0;

    // Initial average gain/loss (first `period` changes)
    for (let i = 1; i <= period; i++) {
      const change = src[i] - src[i - 1];
      if (change > 0) avgGain += change;
      else avgLoss += Math.abs(change);
    }
    avgGain /= period;
    avgLoss /= period;

    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    data.push({ time: candles[period].time, value: 100 - 100 / (1 + rs) });

    // Smoothed RSI
    for (let i = period + 1; i < src.length; i++) {
      const change = src[i] - src[i - 1];
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? Math.abs(change) : 0;

      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;

      const rsi = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
      data.push({ time: candles[i].time, value: rsi });
    }

    return [{ key: "rsi", data }];
  },
};
